import { ChangeEvent, FC, useEffect, useReducer, useState } from 'react';
import { Container } from './MyDataShopkeeper.styles';
import { Button, ErrorScreen, Input, Loading, Section } from '../../../../components';
import { SectionTitle, Separator, Form } from '../../MyAccount.styles';
import { SaveOutlined } from '@mui/icons-material';
import {
	shopkeeperDataReducer,
	shopkeeperDataInitialState,
	shopDataInitialState,
	shopDataReducer,
	tranformResponseIntoForm,
} from './forms';
import { useToastStore, useUserStore } from '../../../../stores';
import {
	putUser,
	putShopkeeper,
	TRevenda,
	getShopkeeperById,
	TUserUpdate,
	TUser,
} from '../../../../services';

const MyDataShopkeeper: FC = () => {
	const { user, setUser } = useUserStore();
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [shopDataResponse, setShopDataResponse] = useState<TRevenda>({} as TRevenda);
	const [shopkeeperDataState, shopkeeperDataDispatch] = useReducer(
		shopkeeperDataReducer,
		shopkeeperDataInitialState
	);
	const [shopDataState, shopDataDispatch] = useReducer(shopDataReducer, shopDataInitialState);
	const tiposDeDocumento = { 1: 'CPF', 2: 'CNPJ' };

	const handleShopkeeperInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const { name, value } = e.target;
		shopkeeperDataDispatch({ type: 'SET_FIELD_VALUE', field: name, payload: value });
	};

	const handleShopDataInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const { name, value } = e.target;
		shopDataDispatch({ type: 'SET_FIELD_VALUE', field: name, payload: value });
	};

	const handleUpdateBasicData = async () => {
		const newUserData: TUser = {
			...user!,
			nome: shopkeeperDataState.nome,
			email: shopkeeperDataState.email,
			telefone: shopkeeperDataState.telefone,
		};
		const body: TUserUpdate = {
			id: user?.id || 0,
			nome: shopkeeperDataState.nome,
			email: shopkeeperDataState.email,
			telefone: shopkeeperDataState.telefone,
		};

		try {
			await putUser(body);
			setUser(newUserData);
			setToast({ message: 'Dados atualizados com sucesso!', variant: 'success' });
		} catch {
			setToast({ message: 'Erro ao atualizar dados!', variant: 'error' });
		}
	};

	const handleUpdateShopData = async () => {
		const body: TRevenda = { ...shopDataResponse, ...shopDataState };

		try {
			await putShopkeeper(body);
			const userUpdated = {
				...user!,
				revenda: body,
			};
			setUser(userUpdated);
			setToast({ message: 'Dados da loja atualizados com sucesso!', variant: 'success' });
		} catch {
			setToast({ message: 'Erro ao atualizar dados da loja!', variant: 'error' });
		}
	};

	useEffect(() => {
		setLoading(true);

		const fetchShopkeeperData = async () => {
			if (!user || !user?.revenda?.id) return;

			const newShopkeeperState = { nome: user.nome, telefone: user.telefone, email: user.email };
			shopkeeperDataDispatch({ type: 'REPLACE_STATE', payload: newShopkeeperState });

			try {
				const id = user.revenda.id.toString();
				const response = await getShopkeeperById({ id });

				const newFormState = tranformResponseIntoForm(response!);
				shopDataDispatch({ type: 'REPLACE_STATE', payload: newFormState });

				setShopDataResponse(response!);
			} catch {
				setError(true);
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchShopkeeperData();
	}, [user, user?.revenda?.id]);

	if (loading) return <Loading fullScreen />;

	if (error) return <ErrorScreen fullScreen />;

	return (
		<Container>
			<Section>
				<SectionTitle>Meus dados</SectionTitle>
				<Separator />
				<Form>
					<Input
						variant='text'
						label='Nome'
						className='full-line'
						value={shopkeeperDataState.nome}
						onChange={handleShopkeeperInputChange}
						name='nome'
					/>
					<Input
						variant='text'
						label='Login/E-mail'
						className='left-half'
						value={shopkeeperDataState.email}
						onChange={handleShopkeeperInputChange}
						name='email'
					/>
					<Input
						variant='text'
						label='Telefone'
						className='right-half'
						value={shopkeeperDataState.telefone}
						onChange={handleShopkeeperInputChange}
						maxLength={15}
						name='telefone'
					/>
					<Button
						variant='contained'
						onClick={handleUpdateBasicData}
						fitContent
						disabled={
							!shopkeeperDataState.nome ||
							!shopkeeperDataState.email ||
							!shopkeeperDataState.telefone
						}
					>
						<SaveOutlined fontSize='medium' />
						<span>Salvar</span>
					</Button>
				</Form>
			</Section>
			<Section>
				<SectionTitle>Informações da loja</SectionTitle>
				<Separator />
				<Form>
					<Input
						variant='text'
						label='Razão Social'
						className='full-line'
						value={shopDataState.razaoSocial}
						onChange={handleShopDataInputChange}
						name='razaoSocial'
					/>
					<Input
						variant='text'
						label='Nome Fantasia'
						className='full-line'
						value={shopDataState.fantasia}
						onChange={handleShopDataInputChange}
						name='fantasia'
					/>
					<Input
						variant='text'
						label={
							tiposDeDocumento[
								shopDataState.tipoDocumento as unknown as keyof typeof tiposDeDocumento
							]
						}
						className='full-line'
						value={shopDataState.numeroDocumento}
						onChange={handleShopDataInputChange}
						name='numeroDocumento'
						readOnly
					/>
					<Input
						variant='text'
						label='E-mail'
						className='full-line'
						value={shopDataState.email}
						onChange={handleShopDataInputChange}
						name='email'
					/>
					<Input
						variant='text'
						label='Telefone'
						className='left-half'
						value={shopDataState.telefone}
						onChange={handleShopDataInputChange}
						name='telefone'
						maxLength={14}
					/>
					<Input
						variant='text'
						label='Celular'
						className='right-half'
						value={shopDataState.celular}
						onChange={handleShopDataInputChange}
						name='celular'
						maxLength={15}
					/>
					<Input
						variant='text'
						label='CEP'
						className='full-line'
						value={shopDataState.cep}
						onChange={handleShopDataInputChange}
						name='cep'
						maxLength={9}
					/>
					<Input
						variant='text'
						label='Logradouro'
						className='full-line'
						value={shopDataState.logradouro}
						onChange={handleShopDataInputChange}
						name='logradouro'
					/>
					<Input
						variant='text'
						label='Número'
						className='left-half'
						value={shopDataState.numeroLogradouro}
						onChange={handleShopDataInputChange}
						name='numeroLogradouro'
					/>
					<Input
						variant='text'
						label='Complemento'
						className='right-half'
						value={shopDataState.complementoLogradouro}
						onChange={handleShopDataInputChange}
						name='complementoLogradouro'
					/>
					<Input
						variant='text'
						label='Bairro'
						className='full-line'
						value={shopDataState.bairro}
						onChange={handleShopDataInputChange}
						name='bairro'
					/>
					<Input
						variant='text'
						label='Cidade'
						className='left-half'
						value={shopDataState.cidade}
						onChange={handleShopDataInputChange}
						name='cidade'
					/>
					<Input
						variant='text'
						label='Estado'
						className='right-half'
						value={shopDataState.estado}
						onChange={handleShopDataInputChange}
						name='estado'
					/>
					<Button variant='contained' onClick={handleUpdateShopData} fitContent>
						<SaveOutlined fontSize='medium' />
						<span>Salvar</span>
					</Button>
				</Form>
			</Section>
		</Container>
	);
};

export default MyDataShopkeeper;
